import { useEffect, useMemo, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { ContactShadows } from "@react-three/drei";
import * as THREE from "three";
import { useScroll } from "../../scroll/ScrollProvider";

/**
 * GoldVault — three realistic gold coins struck with the FIVE QUANTUM
 * mark. Beveled lathe body, milled rim, and engraved emblem composited
 * from the brand mark plus arc text on a canvas texture (used as colour
 * + bump map). Spin acceleration ties to scroll.
 */

const COIN_RADIUS = 1.15;
const COIN_THICKNESS = 0.22;
const LOGO_URL = "/brand/five-quantum-gold.png";

function loadLogoImage(): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = LOGO_URL;
  });
}

function paintCoinFace(ctx: CanvasRenderingContext2D, size: number, logo: HTMLImageElement | null) {
  const grad = ctx.createRadialGradient(
    size / 2,
    size / 2,
    size * 0.05,
    size / 2,
    size / 2,
    size / 2,
  );
  grad.addColorStop(0, "#f7d896");
  grad.addColorStop(0.6, "#d8a849");
  grad.addColorStop(1, "#a87421");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);

  ctx.strokeStyle = "rgba(50, 28, 6, 0.85)";
  ctx.lineWidth = size * 0.014;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size * 0.468, 0, Math.PI * 2);
  ctx.stroke();
  ctx.lineWidth = size * 0.005;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size * 0.43, 0, Math.PI * 2);
  ctx.stroke();

  if (logo) {
    const target = size * 0.5;
    const ratio = logo.width > 0 ? logo.height / logo.width : 1;
    const w = target;
    const h = target * ratio;
    ctx.save();
    ctx.globalCompositeOperation = "multiply";
    ctx.globalAlpha = 0.96;
    ctx.drawImage(logo, (size - w) / 2, (size - h) / 2, w, h);
    ctx.restore();
  } else {
    ctx.fillStyle = "rgba(38, 22, 4, 0.92)";
    ctx.font = `700 ${size * 0.18}px "Cinzel","Trajan Pro",serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("5Q", size / 2, size / 2);
  }

  ctx.fillStyle = "rgba(38, 22, 4, 0.7)";
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const r = size * 0.395;
    const cx = size / 2 + Math.cos(a) * r;
    const cy = size / 2 + Math.sin(a) * r;
    ctx.beginPath();
    ctx.arc(cx, cy, size * 0.012, 0, Math.PI * 2);
    ctx.fill();
  }
}

function makeCoinTexture(logo: HTMLImageElement | null): THREE.CanvasTexture {
  const size = 1024;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  paintCoinFace(ctx, size, logo);

  const tex = new THREE.CanvasTexture(canvas);
  tex.anisotropy = 8;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

type CoinSpec = {
  x: number;
  y: number;
  z: number;
  tilt: number;
  spinScale: number;
  phase: number;
  scale: number;
};

export function GoldVault() {
  const root = useRef<THREE.Group>(null);
  const { section, sectionProgress } = useScroll();

  const [logo, setLogo] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    let cancelled = false;
    loadLogoImage()
      .then((img) => {
        if (!cancelled) setLogo(img);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const coinTex = useMemo(() => makeCoinTexture(logo), [logo]);
  useEffect(() => () => coinTex.dispose(), [coinTex]);

  const bodyGeom = useMemo(() => {
    const r = COIN_RADIUS;
    const h = COIN_THICKNESS / 2;
    const bevel = 0.05;
    const points: THREE.Vector2[] = [
      new THREE.Vector2(0, h),
      new THREE.Vector2(r - bevel, h),
      new THREE.Vector2(r, h - bevel),
      new THREE.Vector2(r, -h + bevel),
      new THREE.Vector2(r - bevel, -h),
      new THREE.Vector2(0, -h),
    ];
    return new THREE.LatheGeometry(points, 96);
  }, []);

  const knurlGeom = useMemo(
    () =>
      new THREE.CylinderGeometry(
        COIN_RADIUS + 0.004,
        COIN_RADIUS + 0.004,
        COIN_THICKNESS - 0.03,
        192,
        1,
        true,
      ),
    [],
  );

  const coins = useMemo<CoinSpec[]>(
    () => [
      { x: -2.7, y: 0.45, z: 0.2, tilt: 0.18, spinScale: 0.85, phase: 0.0, scale: 0.92 },
      { x: 0.0, y: -0.05, z: 0.0, tilt: -0.1, spinScale: 1.05, phase: 1.6, scale: 1.0 },
      { x: 2.6, y: 0.6, z: 0.5, tilt: 0.22, spinScale: 0.78, phase: 3.1, scale: 0.88 },
    ],
    [],
  );

  useFrame((state) => {
    if (!root.current) return;
    // Start coming in during Hero's tail, fully assembled by mid-Mandate, and
    // hold steady through Bullion. Coins drift in from the foreground (toward
    // the camera) so they fill the frame while the user is still reading
    // Mandate, even though their resting Z is the deeper Bullion anchor.
    const active =
      section < 1
        ? Math.max(0, sectionProgress * 0.5)
        : section === 1
          ? Math.min(1, 0.35 + sectionProgress * 1.2)
          : 1;
    root.current.userData.assemble = active;
    root.current.userData.time = state.clock.elapsedTime;
    root.current.rotation.y = -0.05 + active * 0.05;
  });

  return (
    <group ref={root} position={[0, -0.15, 0]}>
      {coins.map((spec, i) => (
        <Coin
          key={i}
          spec={spec}
          index={i}
          parent={root}
          bodyGeom={bodyGeom}
          knurlGeom={knurlGeom}
          tex={coinTex}
        />
      ))}

      <ContactShadows
        position={[0, -1.25, 0]}
        opacity={0.55}
        scale={14}
        blur={2.6}
        far={4}
        resolution={512}
        color="#000000"
      />

      <pointLight position={[0, 1.2, 2.6]} intensity={1.6} color="#f6c87a" distance={9} decay={2} />
      <pointLight position={[-3, 0.4, 1.5]} intensity={0.7} color="#f6d28a" distance={6} decay={2} />
    </group>
  );
}

function Coin({
  spec,
  index,
  parent,
  bodyGeom,
  knurlGeom,
  tex,
}: {
  spec: CoinSpec;
  index: number;
  parent: React.RefObject<THREE.Group | null>;
  bodyGeom: THREE.LatheGeometry;
  knurlGeom: THREE.CylinderGeometry;
  tex: THREE.Texture;
}) {
  const ref = useRef<THREE.Group>(null);

  useFrame(() => {
    if (!ref.current || !parent.current) return;
    const a = (parent.current.userData.assemble as number | undefined) ?? 0;
    const t = (parent.current.userData.time as number | undefined) ?? 0;

    const intro = Math.min(1, Math.max(0, a * 2.4 - index * 0.04));
    const eased = 1 - Math.pow(1 - intro, 3);

    const baseSpin = 0.12;
    const spin = (baseSpin + a * 0.45) * spec.spinScale;

    const bobY = Math.sin(t * 0.7 + spec.phase) * 0.09;
    // Drift in from the FOREGROUND (toward camera) so the coins are visible
    // during Mandate even though their resting position is the deeper Bullion
    // anchor. They glide back to spec.z as the camera reaches Bullion.
    const drift = (1 - eased) * 6.5;

    ref.current.position.set(spec.x, spec.y + bobY * eased, spec.z + drift);
    ref.current.rotation.set(
      spec.tilt + Math.sin(t * 0.4 + spec.phase) * 0.05,
      t * spin + spec.phase,
      Math.sin(t * 0.5 + spec.phase * 1.3) * 0.04,
    );
    ref.current.scale.setScalar(spec.scale * (0.55 + eased * 0.45));
    ref.current.visible = eased > 0.001;
  });

  return (
    <group ref={ref} position={[spec.x, spec.y, spec.z]}>
      <mesh geometry={bodyGeom} rotation={[Math.PI / 2, 0, 0]}>
        <meshPhysicalMaterial
          color="#e7c07a"
          metalness={1}
          roughness={0.24}
          clearcoat={0.5}
          clearcoatRoughness={0.2}
          envMapIntensity={1.4}
          emissive="#3a2706"
          emissiveIntensity={0.14}
        />
      </mesh>

      <mesh geometry={knurlGeom} rotation={[Math.PI / 2, 0, 0]}>
        <meshStandardMaterial
          color="#b9892f"
          metalness={1}
          roughness={0.55}
          emissive="#1a1006"
          emissiveIntensity={0.18}
        />
      </mesh>

      <mesh position={[0, 0, COIN_THICKNESS / 2 + 0.001]}>
        <circleGeometry args={[COIN_RADIUS - 0.045, 96]} />
        <meshStandardMaterial
          map={tex}
          bumpMap={tex}
          bumpScale={0.022}
          metalness={0.95}
          roughness={0.32}
          emissive="#3a2a0a"
          emissiveIntensity={0.18}
        />
      </mesh>

      <mesh
        position={[0, 0, -COIN_THICKNESS / 2 - 0.001]}
        rotation={[0, Math.PI, 0]}
      >
        <circleGeometry args={[COIN_RADIUS - 0.045, 96]} />
        <meshStandardMaterial
          map={tex}
          bumpMap={tex}
          bumpScale={0.022}
          metalness={0.95}
          roughness={0.32}
          emissive="#3a2a0a"
          emissiveIntensity={0.18}
        />
      </mesh>

      <mesh position={[0, 0, COIN_THICKNESS / 2 + 0.002]}>
        <torusGeometry args={[COIN_RADIUS * 0.86, 0.01, 12, 128]} />
        <meshStandardMaterial color="#7a5414" metalness={1} roughness={0.45} />
      </mesh>
      <mesh position={[0, 0, -COIN_THICKNESS / 2 - 0.002]}>
        <torusGeometry args={[COIN_RADIUS * 0.86, 0.01, 12, 128]} />
        <meshStandardMaterial color="#7a5414" metalness={1} roughness={0.45} />
      </mesh>
    </group>
  );
}
